import {CommonMethods} from '@PosterWhiteboard/common-methods';
import type {TextShapeObject} from '@PosterWhiteboard/items/text-item/text-shape.types';
import {DEFAULT_TEXT_SHAPE_AMOUNT, DEFAULT_TEXT_SHAPE_COUNT, TextShapeGuideStyle, TextShapeType} from '@PosterWhiteboard/items/text-item/text-shape.types';
import {assertNever} from '@Utils/assert-never.util';

/**
 * The shape a text item's glyphs follow. Plain data with the derived questions the item asks on
 * every layout: does the shape put the text on a path at all, and how the box and guide behave.
 */
export class TextShape extends CommonMethods {
  public type: TextShapeType = TextShapeType.NONE;
  public amount: number = DEFAULT_TEXT_SHAPE_AMOUNT[TextShapeType.NONE];
  public count: number = DEFAULT_TEXT_SHAPE_COUNT;
  public path: string | null = null;

  /** True when the text lays out along a path instead of the wrapped box. */
  public isShaped(): boolean {
    return this.type !== TextShapeType.NONE;
  }

  /** True when the path is one the user drew, so its length is fixed and the text fits to it instead of the box. */
  public isDrawnPath(): boolean {
    return this.type === TextShapeType.PATH;
  }

  /**
   * True when the text runs round the outline of a figure — a circle, a square, a heart — whose
   * size the box sets, as the design draws these shapes: the figure fills the box's shorter side,
   * the text covers the slider's share of its outline, spread out when it is short, and only text
   * that needs more room than that grows the figure (or, in a box the user holds, shrinks the font).
   */
  public isOutlineShape(): boolean {
    return isOutlineShapeType(this.type);
  }

  /**
   * True when the box is the path's own instead of a box the text is fitted into: a drawn path, and
   * a spiral, which keeps its font and winds further out as the text grows. The path's origin
   * stays where it is on the page, the box encloses the whole path, and only the corners resize it.
   */
  public isBoxSizedByPath(): boolean {
    return this.type === TextShapeType.PATH || this.type === TextShapeType.SPIRAL;
  }

  /**
   * True when the shape's path may run longer than the text — a curve that hangs its text inside
   * the ring widens the ring so the letter tops do not crowd, a figure takes the box's size — and
   * the letters then spread along it with extra spacing.
   */
  public spreadsLettersAlongPath(): boolean {
    return this.type === TextShapeType.CURVE || this.isOutlineShape();
  }

  /** True when the text sits centred in the box's spare room whatever its alignment, as the design draws a wave and the figures. */
  public isAlwaysCentredInBox(): boolean {
    return this.type === TextShapeType.WAVE || this.isOutlineShape();
  }

  /** How the path shows itself under the text while the item is selected; a curve and a heart show none, as in the design. */
  public getGuideStyle(): TextShapeGuideStyle {
    switch (this.type) {
      case TextShapeType.NONE:
      case TextShapeType.CURVE:
      case TextShapeType.HEART:
        return TextShapeGuideStyle.NONE;
      case TextShapeType.CIRCLE:
      case TextShapeType.WAVE:
      case TextShapeType.SPIRAL:
      case TextShapeType.SQUARE:
        return TextShapeGuideStyle.SOLID;
      case TextShapeType.PATH:
        return TextShapeGuideStyle.DASHED_WHILE_UNFILLED;
      default:
        return assertNever(this.type);
    }
  }

  public toObject(): TextShapeObject {
    return {
      type: this.type,
      amount: this.amount,
      count: this.count,
      path: this.path,
    };
  }
}

/** True for the shapes that run their text round a figure's outline: a circle, a square, a heart. */
export const isOutlineShapeType = (type: TextShapeType): boolean => {
  return type === TextShapeType.CIRCLE || type === TextShapeType.SQUARE || type === TextShapeType.HEART;
};

/** The whole shape write for picking a type from the UI: the type with that type's starting slider value. PATH is written through {@link getTextShapeObjectForDrawnPath}. */
export const getTextShapeObjectForType = (type: TextShapeType, count: number = DEFAULT_TEXT_SHAPE_COUNT): TextShapeObject => {
  return {
    type,
    amount: DEFAULT_TEXT_SHAPE_AMOUNT[type],
    count,
    path: null,
  };
};

/** The whole shape write for a path the user drew (`path` in the item's units, bounding box at the origin). */
export const getTextShapeObjectForDrawnPath = (path: string): TextShapeObject => {
  return {
    type: TextShapeType.PATH,
    amount: DEFAULT_TEXT_SHAPE_AMOUNT[TextShapeType.PATH],
    count: DEFAULT_TEXT_SHAPE_COUNT,
    path,
  };
};
